import { DateRange, GoalProgressDateRangePreset } from '@griply/types';
import { getDateRangeLabel } from '@griply/utils';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import * as Styled from './date-range-select.style';

export type DateRangeSelectProps = {
  value: GoalProgressDateRangePreset;
  onChange: (value: GoalProgressDateRangePreset) => void;
  customRange?: DateRange;
};

export const DateRangeSelect: React.FC<DateRangeSelectProps> = ({
  value,
  customRange,
  onChange,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const select = (preset: GoalProgressDateRangePreset) => {
    onChange(preset);
    closeMenu();
  };

  const onGoal = () => select(GoalProgressDateRangePreset.Goal);
  const onWeek = () => select(GoalProgressDateRangePreset.Week);
  const onMonth = () => select(GoalProgressDateRangePreset.Month);
  const onQuarter = () => select(GoalProgressDateRangePreset.Quarter);
  const onYear = () => select(GoalProgressDateRangePreset.Year);

  const customRangeLabel = useMemo(
    () => (customRange ? getDateRangeLabel(customRange) : undefined),
    [customRange],
  );

  const labelsMap = useMemo(
    () => ({
      [GoalProgressDateRangePreset.Goal]: customRangeLabel,
      [GoalProgressDateRangePreset.Week]: t(
        'general.labels.goal-progress.date-range.this-week.label',
      ),
      [GoalProgressDateRangePreset.Month]: t(
        'general.labels.goal-progress.date-range.this-month.label',
      ),
      [GoalProgressDateRangePreset.Quarter]: t(
        'general.labels.goal-progress.date-range.this-quarter.label',
      ),
      [GoalProgressDateRangePreset.Year]: t(
        'general.labels.goal-progress.date-range.this-year.label',
      ),
    }),
    [customRangeLabel, t],
  );

  return (
    <Styled.Container>
      <Button
        ref={buttonRef}
        variant={Variants.Text}
        endIcon={ChevronDown}
        onClick={openMenu}
        size={Sizes.Small}
        color={Colors.Inherit}
      >
        {labelsMap[value]}
      </Button>

      {menuOpen && (
        <PopupMenu
          referenceElement={buttonRef}
          onOutside={closeMenu}
          position="bottom-end"
        >
          <PopupMenuList>
            {!!customRange && (
              <PopupMenuListItem>
                <PopupMenuButton
                  onClick={onGoal}
                  start={
                    value === GoalProgressDateRangePreset.Goal ? (
                      <Icon icon={Check} />
                    ) : undefined
                  }
                  indentationLevel={
                    value !== GoalProgressDateRangePreset.Goal ? 1 : 0
                  }
                >
                  {customRangeLabel}
                </PopupMenuButton>
              </PopupMenuListItem>
            )}
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onWeek}
                start={
                  value === GoalProgressDateRangePreset.Week ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={
                  value !== GoalProgressDateRangePreset.Week ? 1 : 0
                }
              >
                {labelsMap[GoalProgressDateRangePreset.Week]}
              </PopupMenuButton>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onMonth}
                start={
                  value === GoalProgressDateRangePreset.Month ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={
                  value !== GoalProgressDateRangePreset.Month ? 1 : 0
                }
              >
                {labelsMap[GoalProgressDateRangePreset.Month]}
              </PopupMenuButton>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onQuarter}
                start={
                  value === GoalProgressDateRangePreset.Quarter ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={
                  value !== GoalProgressDateRangePreset.Quarter ? 1 : 0
                }
              >
                {labelsMap[GoalProgressDateRangePreset.Quarter]}
              </PopupMenuButton>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onYear}
                start={
                  value === GoalProgressDateRangePreset.Year ? (
                    <Icon icon={Check} />
                  ) : undefined
                }
                indentationLevel={
                  value !== GoalProgressDateRangePreset.Year ? 1 : 0
                }
              >
                {labelsMap[GoalProgressDateRangePreset.Year]}
              </PopupMenuButton>
            </PopupMenuListItem>
          </PopupMenuList>
        </PopupMenu>
      )}
    </Styled.Container>
  );
};
